import { getZoneName } from '@/util/zones-util';
import { ZONES } from '@/models/search-filters';

const normalizePlaceName = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const canadianRegionKey = (value) =>
  Object.keys(ZONES.CANADA.zones).find((key) =>
    ZONES.CANADA.zones[key].some((alias) => normalizePlaceName(alias) === normalizePlaceName(value)),
  );

const countryKey = (value) =>
  Object.keys(ZONES).find((key) =>
    ZONES[key].name.some((alias) => normalizePlaceName(alias) === normalizePlaceName(value)),
  );

const localizedRegion = (region, translate, hasTranslation) => {
  if (!region) return region;
  const zoneKey = canadianRegionKey(region) || getZoneName(region);
  const translationKey = `search-filters.zone.values.${zoneKey}`;
  return hasTranslation && hasTranslation(translationKey) ? translate(translationKey) : region;
};

const localizedCountry = (country, translate, hasTranslation) => {
  if (!country) return country;
  const key = countryKey(country);
  const translationKey = key && `search-filters.countries.values.${key}`;
  return translationKey && hasTranslation && hasTranslation(translationKey) ? translate(translationKey) : country;
};

const hasLocationValue = (location) =>
  location &&
  [
    location.streetAddress,
    location.locality,
    location.area,
    location.region,
    location.countryName,
    location.postalCode,
    location.raw,
  ].some((value) => value && value.trim());

export const getEffectiveDeliveryLocations = (tender) => {
  if (!tender) return [];
  const locations = Array.isArray(tender.deliveryLocations) ? tender.deliveryLocations.filter(hasLocationValue) : [];
  if (locations.length) return locations;
  return hasLocationValue(tender.deliveryAddress) ? [tender.deliveryAddress] : [];
};

export const formatDeliveryLocation = (location, translate, hasTranslation) => {
  if (!location) return '';
  const parts = [
    location.streetAddress,
    location.locality,
    location.area,
    localizedRegion(location.region, translate, hasTranslation),
    location.postalCode,
    localizedCountry(location.countryName, translate, hasTranslation),
  ].filter((value) => value && value.trim());
  return parts.length ? [...new Set(parts)].join(', ') : location.raw || '';
};

// A selected or geocoded location has a persisted label. Translate only a whole
// province token in that label; leave street and locality spelling untouched.
export const localizeJobLocationText = (location, translate, hasTranslation) => {
  if (!location || !location.addressText) return location && location.addressText;
  const regionKey = canadianRegionKey(location.region);
  const locationCountryKey = countryKey(location.countryName);
  return location.addressText
    .split(',')
    .map((part) => {
      const token = part.trim();
      if (regionKey && canadianRegionKey(token) === regionKey) {
        return part.replace(token, localizedRegion(location.region, translate, hasTranslation));
      }
      if (locationCountryKey && countryKey(token) === locationCountryKey) {
        return part.replace(token, localizedCountry(location.countryName, translate, hasTranslation));
      }
      return part;
    })
    .join(',');
};
